import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";

const INDUSTRIES = ["Banking and financial services", "Retail", "FMCG and consumer goods", "Mining and natural resources",
  "Insurance", "Manufacturing", "Healthcare", "Telecommunications", "Technology and professional services", "Education", "Government and public services"];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: "", industry: INDUSTRIES[0], org_type: "private", regions: "", revenue: "", headcount: "", strategic_priorities: "", pain_points: "" });
  const [available, setAvailable] = useState([]);
  const [currentIndustry, setCurrentIndustry] = useState(null);
  const [loadModel, setLoadModel] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/organization").then((r) => {
      const o = r.data;
      setCurrentIndustry(o.industry);
      setForm({ name: o.name, industry: o.industry, org_type: o.org_type, regions: o.regions.join(", "), revenue: o.revenue,
        headcount: String(o.headcount), strategic_priorities: o.strategic_priorities.join("\n"), pain_points: o.pain_points.join("\n") });
    }).catch(() => {});
    api.get("/reference-models").then((r) => setAvailable(r.data.available)).catch(() => {});
  }, []);

  const hasModel = available.includes(form.industry);
  const industryChanged = currentIndustry && form.industry !== currentIndustry;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const finish = async () => {
    setSaving(true);
    await api.put("/organization", {
      name: form.name, industry: form.industry, org_type: form.org_type,
      regions: form.regions.split(",").map((s) => s.trim()).filter(Boolean),
      revenue: form.revenue, headcount: parseInt(form.headcount) || 0,
      strategic_priorities: form.strategic_priorities.split("\n").filter(Boolean),
      pain_points: form.pain_points.split("\n").filter(Boolean), onboarded: true,
    });
    if (loadModel && hasModel) {
      try {
        await api.post("/reference-models/apply", { industry: form.industry });
        toast.success(`${form.industry} reference model loaded as a starting hypothesis — every element is editable.`);
      } catch (e) {
        toast.error("Could not load the reference model");
      }
    } else {
      toast.success("Organisation profile saved.");
    }
    setSaving(false);
    navigate("/map");
  };

  const input = "w-full border border-black/15 bg-white rounded-sm px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-[#D4AF37]";
  const steps = ["Identity", "Scale & context", "Priorities & pain"];

  return (
    <div className="min-h-screen bg-[#FDFBF7] canvas-grain flex items-center justify-center p-8" data-testid="onboarding-page">
      <div className="w-full max-w-xl bg-white border border-black/10 rounded-sm p-8 fade-up">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">Guided onboarding</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1 mb-2">Set up your organisation</h1>
        <div className="flex gap-2 mb-6">
          {steps.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1 rounded-full ${i <= step ? "bg-[#D4AF37]" : "bg-black/10"}`} />
              <p className={`text-[10px] mt-1 ${i <= step ? "text-black" : "text-black/40"}`}>{s}</p>
            </div>
          ))}
        </div>
        {step === 0 && (
          <div className="space-y-3">
            <input className={input} placeholder="Organisation name" value={form.name} onChange={set("name")} data-testid="onboarding-name-input" />
            <select className={input} value={form.industry} onChange={(e) => { setForm({ ...form, industry: e.target.value }); setLoadModel(available.includes(e.target.value) && e.target.value !== currentIndustry); }} data-testid="onboarding-industry-select">
              {INDUSTRIES.map((i) => <option key={i} value={i}>{i}{available.includes(i) ? " — ★ starter model" : ""}</option>)}
            </select>
            <select className={input} value={form.org_type} onChange={set("org_type")} data-testid="onboarding-type-select">
              {["private", "public", "government", "NGO", "hybrid"].map((t) => <option key={t}>{t}</option>)}
            </select>
            {hasModel ? (
              <label className="flex items-start gap-2 text-[12px] text-black/70 border border-[#D4AF37]/50 bg-[#D4AF37]/5 rounded-sm p-3 cursor-pointer" data-testid="load-model-toggle">
                <input type="checkbox" checked={loadModel} onChange={(e) => setLoadModel(e.target.checked)} className="mt-0.5 accent-[#D4AF37]" data-testid="load-model-checkbox" />
                <span><b>Load the {form.industry} starter reference model.</b> This replaces the current reference data (departments, capabilities, workflows, roles, systems, data, controls, diagnostics, scenarios and roadmap) with an industry starting hypothesis. Workshop outcomes and the audit trail are kept.</span>
              </label>
            ) : (
              <p className="text-[11px] text-black/50">No starter model for this industry yet — you'll keep the current structure and can reshape it manually.</p>
            )}
            <p className="text-[11px] text-black/50">Industry content is a starting hypothesis, never an assumption that all organisations are identical.</p>
          </div>
        )}
        {step === 1 && (
          <div className="space-y-3">
            <input className={input} placeholder="Operating regions (comma separated)" value={form.regions} onChange={set("regions")} data-testid="onboarding-regions-input" />
            <input className={input} placeholder="Annual revenue (e.g. R18.4bn)" value={form.revenue} onChange={set("revenue")} data-testid="onboarding-revenue-input" />
            <input className={input} placeholder="Headcount" type="number" value={form.headcount} onChange={set("headcount")} data-testid="onboarding-headcount-input" />
          </div>
        )}
        {step === 2 && (
          <div className="space-y-3">
            <label className="text-xs text-black/60">Strategic priorities (one per line)</label>
            <textarea className={input} rows={4} value={form.strategic_priorities} onChange={set("strategic_priorities")} data-testid="onboarding-priorities-input" />
            <label className="text-xs text-black/60">Known pain points (one per line)</label>
            <textarea className={input} rows={4} value={form.pain_points} onChange={set("pain_points")} data-testid="onboarding-pain-input" />
          </div>
        )}
        <div className="flex justify-between mt-6">
          <button onClick={() => (step === 0 ? navigate("/") : setStep(step - 1))} data-testid="onboarding-back-btn"
            className="text-sm border border-black/20 rounded-sm px-4 py-2 hover:border-[#D4AF37] transition-colors">{step === 0 ? "Skip" : "Back"}</button>
          {step < 2 ? (
            <button onClick={() => setStep(step + 1)} data-testid="onboarding-next-btn" className="text-sm bg-[#0A0A0A] text-[#D4AF37] rounded-sm px-5 py-2 font-semibold hover:bg-black/85 transition-colors">Next</button>
          ) : (
            <button onClick={finish} disabled={saving} data-testid="onboarding-finish-btn" className="text-sm bg-[#0A0A0A] text-[#D4AF37] rounded-sm px-5 py-2 font-semibold hover:bg-black/85 transition-colors disabled:opacity-60">
              {saving ? "Saving…" : loadModel && hasModel ? "Finish & load reference model" : "Finish"}</button>
          )}
        </div>
      </div>
    </div>
  );
}
